import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { FiEye, FiUser, FiSettings, FiBarChart2, FiLogOut, FiX, FiCheck, FiAlertCircle, FiClock, FiMapPin, FiPhone, FiStar, FiPackage, FiDollarSign, FiHome, FiShoppingBag, FiScissors, FiCoffee, FiMail, FiHash, FiTrendingUp } from 'react-icons/fi';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { BiStore, BiTime, BiCodeAlt } from 'react-icons/bi';
import { HiOutlineBuildingOffice } from "react-icons/hi2";
import { apiCall } from '../config/api-request';
import { useNavigate } from 'react-router-dom';


function Dashboard() {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('hotels');
    const [profileDropdown, setProfileDropdown] = useState(false);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [selectedItemType, setSelectedItemType] = useState('');
    const [selectedAction, setSelectedAction] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [modalClosing, setModalClosing] = useState(false);
   const [sessionData, setSessionData] = useState(() => {
        const data = localStorage.getItem('session_data');
        return data ? JSON.parse(data) : null;
    });

    // State for API data
    const [dashboardData, setDashboardData] = useState({
        summary: [],
        orders: [],
        pending_hotels: [],
        pending_restaurant: [],
        pending_salon: []
    });

    // Get summary data
    const summary = dashboardData.summary[0] || {};
    const totalRestaurants = parseInt(summary.total_restaurant) || 0;
    const totalHotels = parseInt(summary.total_hotel) || 0;
    const totalSalons = parseInt(summary.total_salon) || 0;
    const totalSales = parseFloat(summary.total_sale) || 0;

    // Calculate stats from real data
    const totalOrders = dashboardData.orders.length;
    const totalRevenue = dashboardData.orders.reduce((sum, order) => sum + parseFloat(order.amount), 0);
    const pendingCount = dashboardData.pending_hotels.length +
        dashboardData.pending_restaurant.length +
        dashboardData.pending_salon.length;

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'accept': return 'bg-green-100 text-green-800';
            case 'waiting': return 'bg-yellow-100 text-yellow-800';
            case 'pending': return 'bg-yellow-100 text-yellow-800';
            case 'delivered': return 'bg-green-100 text-green-800';
            case 'processing': return 'bg-blue-100 text-blue-800';
            case 'shipped': return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getTypeIcon = (type) => {
        switch (type?.toLowerCase()) {
            case 'hotel service': return <FiHome className="w-4 h-4" />;
            case 'food item': return <FiShoppingBag className="w-4 h-4" />;
            default: return <FiPackage className="w-4 h-4" />;
        }
    };

    const handleViewDetails = (item, type) => {
        setSelectedItem(item);
        setSelectedItemType(type);
        setSelectedAction('');
        setShowModal(true);
        setModalClosing(false);
    };

    const handleActionSubmit = async () => {
        if (!selectedAction) {
            toast.error("Please select an action");
            return;
        }

        setActionLoading(true);

        try {
            const result = await apiCall(
                {
                    api: 'process-action-admin',
                    type: selectedItemType,
                    code: selectedItem.code || selectedItem.id,
                    action: selectedAction
                },
                true
            );

            if (result.status === true) {
                toast.success(`Action "${selectedAction}" completed successfully`);
                handleCloseModal();
                // Refresh dashboard data
                await fetchDashboard();
            } else {
                toast.error(result.message || "Failed to process action");
            }
        } catch (error) {
            console.error("Error processing action:", error);
            toast.error("Failed to process action");
        } finally {
            setActionLoading(false);
        }
    };

    const handleProfileMenu = (action) => {
        setProfileDropdown(false);
        if (action === 'Logout') {
            localStorage.clear();
            sessionStorage.clear();
            window.dispatchEvent(new CustomEvent('userLoggedOut'));
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            };
        } else {
            toast.info(`${action} clicked`);
        }
    };

    const getCurrentData = () => {
        switch (activeTab) {
            case 'hotels': return dashboardData.pending_hotels;
            case 'restaurants': return dashboardData.pending_restaurant;
            case 'salons': return dashboardData.pending_salon;
            default: return dashboardData.pending_hotels;
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(parseInt(timestamp) * 1000);
        return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const formatTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = new Date(parseInt(timestamp) * 1000);
        return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    };

    const fetchDashboard = async () => {
        try {
            setLoading(true);
            const result = await apiCall(
                {
                    api: 'dashboard-data-admin'
                },
                true
            );

            if (result.status === true) {
                setDashboardData({
                    summary: result.data.summary || [],
                    orders: result.data.orders || [],
                    pending_hotels: result.data.pending_hotels || [],
                    pending_restaurant: result.data.pending_restaurant || [],
                    pending_salon: result.data.pending_salon || []
                });
            } else {
                toast.error(result.message || "Failed to load dashboard data");
            }
        } catch (error) {
            console.error("Error fetching dashboard:", error);
            toast.error("Failed to load dashboard data");
        } finally {
            setLoading(false);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.profile-dropdown')) {
                setProfileDropdown(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    useEffect(() => {
        fetchDashboard();
    }, []);

    // Close modal on escape key
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && showModal && !modalClosing) {
                handleCloseModal();
            }
        };
        document.addEventListener('keydown', handleEscKey);
        return () => document.removeEventListener('keydown', handleEscKey);
    }, [showModal, modalClosing]);

    const handleCloseModal = () => {
        setModalClosing(true);
        setTimeout(() => {
            setShowModal(false);
            setSelectedAction('');
            setSelectedItem(null);
            setSelectedItemType('');
            setActionLoading(false);
            setModalClosing(false);
        }, 200);
    };

    // Skeleton Loader Component
    const SkeletonCard = () => (
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-gray-200 animate-pulse">
            <div className="flex items-center justify-between">
                <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                    <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="bg-gray-200 rounded-full p-3 w-12 h-12"></div>
            </div>
        </div>
    );

    const SkeletonTableRow = () => (
        <tr className="animate-pulse">
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20"></div></td>
            <td className="px-6 py-4"><div className="h-8 bg-gray-200 rounded w-20"></div></td>
        </tr>
    );

    const SkeletonApprovalRow = () => (
        <tr className="animate-pulse">
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-28"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24"></div></td>
            <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-40"></div></td>
            <td className="px-6 py-4"><div className="h-6 bg-gray-200 rounded w-20"></div></td>
            <td className="px-6 py-4"><div className="h-8 bg-gray-200 rounded w-24"></div></td>
        </tr>
    );

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Fixed Header */}
            <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo */}
                        <div className="flex items-center">
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg p-2 shadow-md">
                                <FiPackage className="w-6 h-6 text-white" />
                            </div>
                            <span className="ml-3 text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">Awiss</span>
                        </div>

                        {/* Profile Dropdown */}
                        <div className="relative profile-dropdown">
                            <button
                                onClick={() => setProfileDropdown(!profileDropdown)}
                                className="flex items-center space-x-2 focus:outline-none bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg transition"
                            >
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center shadow-md">
                                    <span className="text-white text-sm font-medium">AD</span>
                                </div>
                                <FiUser className="w-4 h-4 text-gray-600" />
                                <svg className={`w-4 h-4 text-gray-600 transition-transform ${profileDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {profileDropdown && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-100">
                                    <div className="px-4 py-3 border-b border-gray-100">
                                        <p className="text-sm font-semibold text-gray-800">{sessionData?.name || 'Username'}</p>
                                        <p className="text-xs text-gray-500">{sessionData?.email || 'demo@hotmail.com'}</p>
                                    </div>

                                    <div className="border-t border-gray-100 mt-1 pt-1">
                                        <button
                                            onClick={() => handleProfileMenu('Logout')}
                                            className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center space-x-3 transition"
                                        >
                                            <FiLogOut className="w-5 h-5 text-red-500" />
                                            <span>Logout</span>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="pt-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    {/* Stats Cards with Skeleton Loading */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {loading ? (
                            <>
                                <SkeletonCard />
                                <SkeletonCard />
                                <SkeletonCard />
                                <SkeletonCard />
                            </>
                        ) : (
                            <>
                                {/* Total Hotels Card */}
                                <div
                                    onClick={() => navigate('../hotels')}
                                    className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500 hover:shadow-lg transition cursor-pointer group transform hover:scale-105 transition-transform duration-200"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500 font-medium">Total Hotels</p>
                                            <p className="text-2xl font-bold text-gray-800 mt-1">{totalHotels}</p>
                                        </div>
                                        <div className="bg-blue-100 rounded-full p-3 group-hover:bg-blue-200 transition">
                                            <FiHome className="w-6 h-6 text-blue-600" />
                                        </div>
                                    </div>
                                </div>

                                {/* Total Restaurants Card */}
                                <div
                                    onClick={() => navigate('../restaurants')}
                                    className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500 hover:shadow-lg transition cursor-pointer group transform hover:scale-105 transition-transform duration-200"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500 font-medium">Total Restaurants</p>
                                            <p className="text-2xl font-bold text-gray-800 mt-1">{totalRestaurants}</p>
                                        </div>
                                        <div className="bg-green-100 rounded-full p-3 group-hover:bg-green-200 transition">
                                            <FiCoffee className="w-6 h-6 text-green-600" />
                                        </div>
                                    </div>
                                </div>

                                {/* Total Salons Card */}
                                <div
                                    onClick={() => navigate('../salons')}
                                    className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500 hover:shadow-lg transition cursor-pointer group transform hover:scale-105 transition-transform duration-200"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500 font-medium">Total Salons</p>
                                            <p className="text-2xl font-bold text-gray-800 mt-1">{totalSalons}</p>
                                        </div>
                                        <div className="bg-purple-100 rounded-full p-3 group-hover:bg-purple-200 transition">
                                            <FiScissors className="w-6 h-6 text-purple-600" />
                                        </div>
                                    </div>
                                </div>

                                {/* Total Revenue Card */}
                                <div
                                    onClick={() => navigate('../order')}
                                    className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500 hover:shadow-lg transition group cursor-pointer">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm text-gray-500 font-medium">Today Sale</p>
                                            <p className="text-2xl font-bold text-gray-800 mt-1">₹{totalSales}</p>
                                        </div>
                                        <div className="bg-orange-100 rounded-full p-3 group-hover:bg-orange-200 transition">
                                            <FiDollarSign className="w-6 h-6 text-orange-600" />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Recent Orders Table with Skeleton Loading */}
                    <div className="bg-white rounded-xl shadow-md mb-8 overflow-hidden">
                        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-green-700">
                            <div>
                                <h2 className="text-lg font-semibold text-white">Recent Orders</h2>
                            </div>
                            <p className="text-sm text-black bg-gray-100 p-2 rounded cursor-pointer" onClick={() => navigate('../order-list')}>View All</p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[800px]">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {loading ? (
                                        <>
                                            <SkeletonTableRow />
                                            <SkeletonTableRow />
                                            <SkeletonTableRow />
                                            <SkeletonTableRow />
                                            <SkeletonTableRow />
                                        </>
                                    ) : (
                                        <>
                                            {dashboardData.orders.map((order) => (
                                                <tr key={order.order_id} className="hover:bg-gray-50 transition">
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{order.order_id}</td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex items-center space-x-2">
                                                            {getTypeIcon(order.type)}
                                                            <span className="text-sm text-gray-600">{order.type}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-semibold text-gray-800">₹{parseFloat(order.amount).toLocaleString()}</td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(order.status)}`}>
                                                            {order.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">{order.date}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">{formatTime(order.time)}</td>
                                                    <td className="px-6 py-4">
                                                        <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center space-x-1" onClick={() => navigate(`../order-details?order_id=${order.order_id}`)}>
                                                            <FiEye className="w-4 h-4" />
                                                            <span>View</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {dashboardData.orders.length === 0 && (
                                                <tr>
                                                    <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                                        No orders found
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pending Approvals Section with Skeleton Loading */}
                    <div id="pending-approvals" className="bg-white rounded-xl shadow-md overflow-hidden">
                        <div className="border-b border-green-600 px-6 pt-4 bg-gradient-to-r from-green-700 to-green-800">
                            <h2 className="text-lg font-semibold text-white mb-4">Pending Approvals</h2>
                            {/* Tabs */}
                            <div className="flex space-x-6">
                                <button
                                    onClick={() => setActiveTab('hotels')}
                                    className={`pb-3 px-1 text-sm font-medium transition border-b-2 flex items-center space-x-2 ${activeTab === 'hotels'
                                        ? 'border-white text-white'
                                        : 'border-transparent text-green-100 hover:text-white hover:border-green-300'
                                        }`}
                                    disabled={loading}
                                >
                                    <FiHome className="w-4 h-4" />
                                    <span>Hotels ({loading ? '...' : dashboardData.pending_hotels.length})</span>
                                </button>
                                <button
                                    onClick={() => setActiveTab('restaurants')}
                                    className={`pb-3 px-1 text-sm font-medium transition border-b-2 flex items-center space-x-2 ${activeTab === 'restaurants'
                                        ? 'border-white text-white'
                                        : 'border-transparent text-green-100 hover:text-white hover:border-green-300'
                                        }`}
                                    disabled={loading}
                                >
                                    <FiCoffee className="w-4 h-4" />
                                    <span>Restaurants ({loading ? '...' : dashboardData.pending_restaurant.length})</span>
                                </button>
                                <button
                                    onClick={() => setActiveTab('salons')}
                                    className={`pb-3 px-1 text-sm font-medium transition border-b-2 flex items-center space-x-2 ${activeTab === 'salons'
                                        ? 'border-white text-white'
                                        : 'border-transparent text-green-100 hover:text-white hover:border-green-300'
                                        }`}
                                    disabled={loading}
                                >
                                    <FiScissors className="w-4 h-4" />
                                    <span>Salons ({loading ? '...' : dashboardData.pending_salon.length})</span>
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[800px]">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rating</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {loading ? (
                                        <>
                                            <SkeletonApprovalRow />
                                            <SkeletonApprovalRow />
                                            <SkeletonApprovalRow />
                                        </>
                                    ) : (
                                        <>
                                            {getCurrentData().map((item) => (
                                                <tr key={item.code || item.id} className="hover:bg-gray-50 transition">
                                                    <td className="px-6 py-4 text-sm font-mono text-gray-600">
                                                        <div className="flex items-center space-x-2">
                                                            <span>{item.code}</span>
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{item.name}</td>
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        <div className="flex items-center space-x-1">
                                                            <FiStar className="w-4 h-4 text-yellow-400 fill-current" />
                                                            <span>{item.rating}</span>
                                                            {item.review && item.review !== '0' && (
                                                                <span className="ml-1 text-xs text-gray-500">({item.review} reviews)</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-500">
                                                        <div className="flex items-center space-x-1">
                                                            <HiOutlineLocationMarker className="w-4 h-4 text-gray-400" />
                                                            <span>{item.location || 'N/A'}</span>
                                                        </div>
                                                        {item.longitude && item.latitude && (
                                                            <div className="text-xs text-gray-400 mt-1">
                                                                {parseFloat(item.latitude).toFixed(4)}, {parseFloat(item.longitude).toFixed(4)}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className={`px-2 py-1 text-xs rounded-full ${item.admin_status === '0' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                                            {item.admin_status === '0' ? 'Pending' : 'Approved'}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <button
                                                            onClick={() => handleViewDetails(item, activeTab.slice(0, -1))}
                                                            className="px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm rounded-lg hover:from-blue-600 hover:to-blue-700 transition flex items-center space-x-2 shadow-md"
                                                        >
                                                            <FiEye className="w-4 h-4" />
                                                            <span>View Details</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {getCurrentData().length === 0 && (
                                                <tr>
                                                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                                        No pending {activeTab} found
                                                    </td>
                                                </tr>
                                            )}
                                        </>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </main>

            {/* Smooth Modal */}
            {(showModal || modalClosing) && selectedItem && (
                <div
                    className={`fixed inset-0 z-50 overflow-y-auto transition-all duration-300 ${modalClosing ? 'opacity-0' : 'opacity-100'
                        }`}
                >
                    <div className="flex items-center justify-center min-h-screen px-4 py-8">
                        {/* Backdrop */}
                        <div
                            className={`fixed inset-0 bg-black transition-opacity duration-300 ${modalClosing ? 'opacity-0' : 'opacity-50'
                                }`}
                            onClick={handleCloseModal}
                        ></div>

                        {/* Modal Container - Centered with max width */}
                        <div
                            className={`relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col transform transition-all duration-300 ${modalClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
                                }`}
                        >
                            {/* Fixed Header */}
                            <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-2xl px-6 py-4 flex-shrink-0">
                                <div className="flex justify-between items-center">
                                    <div>
                                        <h3 className="text-xl font-bold text-white">
                                            {selectedItem.name}
                                        </h3>
                                        <p className="text-blue-100 text-sm mt-1 capitalize">{selectedItemType} Details</p>
                                    </div>
                                    <button
                                        onClick={handleCloseModal}
                                        className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                                    >
                                        <FiX className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            {/* Scrollable Body */}
                            <div className="flex-1 overflow-y-auto px-6 py-4">
                                <div className="space-y-4">
                                    {/* Vendor Information */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                            <BiStore className="w-4 h-4 mr-2" />
                                            Vendor Information
                                        </h4>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div className="text-gray-500">Code:</div>
                                            <div className="text-gray-900 font-mono">{selectedItem.code}</div>

                                            <div className="text-gray-500">Name:</div>
                                            <div className="text-gray-900 font-medium">{selectedItem.name}</div>

                                            <div className="text-gray-500">Rating:</div>
                                            <div className="text-gray-900">{selectedItem.rating} ⭐</div>

                                            {selectedItem.review && selectedItem.review !== '0' && (
                                                <>
                                                    <div className="text-gray-500">Reviews:</div>
                                                    <div className="text-gray-900">{selectedItem.review}</div>
                                                </>
                                            )}

                                            <div className="text-gray-500">Status:</div>
                                            <div className="text-gray-900">{selectedItem.status === '1' ? 'Active' : 'Inactive'}</div>

                                            <div className="text-gray-500">Admin Status:</div>
                                            <div className={`font-semibold ${selectedItem.admin_status === '0' ? 'text-yellow-600' : 'text-green-600'}`}>
                                                {selectedItem.admin_status === '0' ? 'Pending' : 'Approved'}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Location Information */}
                                    {(selectedItem.location || selectedItem.longitude || selectedItem.latitude) && (
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                                <FiMapPin className="w-4 h-4 mr-2" />
                                                Location Information
                                            </h4>
                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                {selectedItem.location && (
                                                    <>
                                                        <div className="text-gray-500">Location:</div>
                                                        <div className="text-gray-900">{selectedItem.location}</div>
                                                    </>
                                                )}
                                                {selectedItem.latitude && (
                                                    <>
                                                        <div className="text-gray-500">Latitude:</div>
                                                        <div className="text-gray-900 font-mono text-xs">{selectedItem.latitude}</div>
                                                    </>
                                                )}
                                                {selectedItem.longitude && (
                                                    <>
                                                        <div className="text-gray-500">Longitude:</div>
                                                        <div className="text-gray-900 font-mono text-xs">{selectedItem.longitude}</div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Service Details */}
                                    {(selectedItem.delivery_min_time || selectedItem.waiting_min_time || selectedItem.is_open) && (
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                                <BiTime className="w-4 h-4 mr-2" />
                                                Service Details
                                            </h4>
                                            <div className="grid grid-cols-2 gap-3 text-sm">
                                                {selectedItem.delivery_min_time && (
                                                    <>
                                                        <div className="text-gray-500">Delivery Time:</div>
                                                        <div className="text-gray-900">{selectedItem.delivery_min_time} - {selectedItem.delivery_max_time} min</div>
                                                    </>
                                                )}
                                                {selectedItem.waiting_min_time && (
                                                    <>
                                                        <div className="text-gray-500">Waiting Time:</div>
                                                        <div className="text-gray-900">{selectedItem.waiting_min_time} - {selectedItem.waiting_max_time} min</div>
                                                    </>
                                                )}
                                                {selectedItem.is_open && (
                                                    <>
                                                        <div className="text-gray-500">Status:</div>
                                                        <div className="text-gray-900">{selectedItem.is_open}</div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* User Information */}
                                    <div className="bg-gray-50 rounded-lg p-4">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                            <FiUser className="w-4 h-4 mr-2" />
                                            User Information
                                        </h4>
                                        <div className="grid grid-cols-2 gap-3 text-sm">
                                            <div className="text-gray-500">User ID:</div>
                                            <div className="text-gray-900">{selectedItem.user_id}</div>

                                            <div className="text-gray-500">Name:</div>
                                            <div className="text-gray-900 font-medium">{selectedItem.user_name}</div>

                                            <div className="text-gray-500">Email:</div>
                                            <div className="text-gray-900 text-xs break-all">{selectedItem.user_email}</div>

                                            <div className="text-gray-500">Mobile:</div>
                                            <div className="text-gray-900">{selectedItem.user_mobile}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Fixed Footer with Simple Actions */}
                            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-2xl flex-shrink-0">
                                <div className="flex flex-col sm:flex-row gap-3">
                                    <button
                                        onClick={() => setSelectedAction('approve')}
                                        className={`flex-1 px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium ${selectedAction === 'approve'
                                            ? 'bg-green-600 text-white shadow-md scale-105'
                                            : 'bg-green-100 text-green-700 hover:bg-green-200'
                                            }`}
                                    >
                                        <FiCheck className="w-4 h-4" />
                                        <span>Approve</span>
                                    </button>
                                    <button
                                        onClick={() => setSelectedAction('reject')}
                                        className={`flex-1 px-4 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium ${selectedAction === 'reject'
                                            ? 'bg-red-600 text-white shadow-md scale-105'
                                            : 'bg-red-100 text-red-700 hover:bg-red-200'
                                            }`}
                                    >
                                        <FiX className="w-4 h-4" />
                                        <span>Reject</span>
                                    </button>
                                    <button
                                        onClick={handleActionSubmit}
                                        disabled={!selectedAction || actionLoading}
                                        className={`px-6 py-2.5 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 text-sm font-medium ${selectedAction && !actionLoading
                                            ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 shadow-md'
                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            }`}
                                    >
                                        {actionLoading ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                                <span>Processing...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>Submit</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: '#363636',
                        color: '#fff',
                    },
                    success: {
                        duration: 3000,
                        iconTheme: {
                            primary: '#10B981',
                            secondary: '#fff',
                        },
                    },
                    error: {
                        duration: 4000,
                        iconTheme: {
                            primary: '#EF4444',
                            secondary: '#fff',
                        },
                    },
                }}
            />
        </div>
    );
}

export default Dashboard;